import { Formik } from 'formik'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from '@10xLabs/react-web/components/Row'
import { getUnprocessableErrors } from 'utils/error'
import { ReactElement } from 'react'
import styled from 'styled-components'
import { schema } from './schema'
import { useMutation } from '@apollo/client'
import { Input } from 'components/Input'
import { InputNumber } from 'components/InputNumber'
import { PHONE_MASK } from 'utils/masks'
import { IResendCouponsModalProps } from './types'
import { isEmpty } from 'lodash'
import { SEND_EMAIL_COUPON, SEND_SMS_COUPON } from 'features/Campaigns/mutations-legacy'

const messages = defineMessages({
  phonePlaceholder: {
    id: 'resendCouponsModal.phone.placeholder',
    defaultMessage: 'Enter a phone number'
  },
  emailPlaceholder: {
    id: 'resendCouponsModal.email.placeholder',
    defaultMessage: 'Enter an email'
  }
})

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`

const ResendCouponsModal = ({
  bookingId,
  email,
  phone,
  closeModal,
  onCompleted,
  campaignId,
  subcampaignId,
  couponId
}: IResendCouponsModalProps): ReactElement => {
  const intl = useIntl()
  const [sendCouponsSMS, { loading: sendCouponsLoading, error: sendSMSError }] = useMutation(SEND_SMS_COUPON, {
    onCompleted: () => {
      onCompleted()
      closeModal()
    }
  })
  const [sendCouponsEmail, { loading: sendEmailLoading, error: sendEmailError }] = useMutation(SEND_EMAIL_COUPON, {
    onCompleted: () => {
      onCompleted()
      closeModal()
    }
  })
  const initialValues = {
    phone,
    email
  }

  const error = sendSMSError || sendEmailError
  const loading = sendEmailLoading || sendCouponsLoading
  return (
    <ModalContainer large>
      <Modal.Header>
        <Heading.H5>
          {bookingId ? (
            <FormattedMessage
              id="trips.resendCouponsModal.title"
              defaultMessage="Resend coupons for booking {id}"
              values={{
                id: bookingId.substring(0, 8)
              }}
            />
          ) : (
            <FormattedMessage id="trips.resendCouponsModal.title2" defaultMessage="Resend coupons " />
          )}
        </Heading.H5>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        onSubmit={(values): void => {
          const variables = {
            campaignID: campaignId,
            subcampaignID: subcampaignId,
            couponID: couponId
          }
          values.phone &&
            sendCouponsSMS({
              variables: {
                input: {
                  ...variables,
                  phone: values.phone
                }
              }
            })
          values.email &&
            sendCouponsEmail({
              variables: {
                input: {
                  ...variables,
                  email: values.email
                }
              }
            })
        }}
        validationSchema={schema(intl)}
      >
        {({ setFieldValue, values, handleSubmit, handleChange, handleBlur, touched, errors }): ReactElement => {
          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <InputNumber
                      type="tel"
                      id="phone"
                      value={values.phone}
                      placeholder={intl.formatMessage(messages.phonePlaceholder)}
                      onChange={(value: string): void => {
                        setFieldValue('phone', value)
                      }}
                      onBlur={handleBlur}
                      mask={PHONE_MASK}
                      placeholderChar=" "
                      label={<FormattedMessage id="dictionary.planId" defaultMessage="Phone" />}
                      touched={touched.phone}
                      errors={errors.phone}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id="email"
                      type="email"
                      placeholder={intl.formatMessage(messages.emailPlaceholder)}
                      label={<FormattedMessage id="dictionary.email" defaultMessage="Email" />}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      touched={touched.email}
                      errors={errors.email}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="trips.resendCouponsModal.error"
                        defaultMessage="There was an error while resending coupons"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="dictionary.close" defaultMessage="Close" />
                  </Button>
                  <Button
                    type="submit"
                    kind="primary"
                    loading={loading}
                    disabled={isEmpty(values.phone) && isEmpty(values.email)}
                  >
                    <FormattedMessage id="trips.resendCouponsModal.action" defaultMessage="Resend coupons" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}

export default ResendCouponsModal
